import { Button, Checkbox, Col, Form, notification, Row, Tabs, Tooltip } from "antd"
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLazyGetUserInfoQuery } from "../../../redux/api/AccountApi";
import { useAppDispatch, useAppSelector } from "../../../redux/Hooks";
import { FloatInput, FloatInputNumber } from "../../common/components/Index";
import { isTokenExpired } from "../../common/ts/CommonFunctions";
import { useLoginOtpMutation, useVerifyOtpLoginMutation } from "../redux/LoginApi";
import { setToken } from "../redux/LoginSlice";
import { setUser } from "../redux/LoginSlice";
import '../assets/css/styles.scss';
import ReCAPTCHA from "react-google-recaptcha";
import { useLazyVerifyGoogleCaptchaQuery } from "../../../redux/api/PublicApi";
import { useLazyGenerateOtpQuery } from "../../forgot-password/redux/ForgotPasswordApi";
import { OTPActions, typeUsername } from "../../common/types/types";

interface ModalProps {
    openRegister: () => void,
    openForgotPassword: () => void,
    setErrorStatus: (isError: boolean, errorMessage?: string) => void,
}

const LoginForm = ({ openRegister, openForgotPassword, setErrorStatus }: ModalProps) => {
    const [form] = Form.useForm();

    const [buttonDisabled, setButtonDisabled] = useState(false);
    const dispatch = useAppDispatch();
    const loginState = useAppSelector(state => state.login);
    const numOfFailedLogins = Number(localStorage.getItem("numOfFailedLogins") || 0);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [currentKey, setCurrentKey] = useState(typeUsername.EMAIL);
    const [placeholder, setPlaceholder] = useState("Email");

    const navigate = useNavigate();

    const [login, { isLoading, isSuccess, data }] = useLoginOtpMutation();
    const [verifyOtpLogin, verifyResponse] = useVerifyOtpLoginMutation();
    const [resendOtp, resendOtpResponse] = useLazyGenerateOtpQuery();
    const [trigger] = useLazyGetUserInfoQuery();
    const [verifyCaptcha] = useLazyVerifyGoogleCaptchaQuery();
    const MESSAGE = "Có thể đăng nhập bằng Email, SĐT, CCCD, CMND, Hộ chiếu hoặc Số ĐKKD";
    // thay uri bằng localhost nếu muốn test bên localhost
    const URI = process.env.REACT_APP_BASE_API_URL;
    // const URI = "http://localhost:3000"

    useEffect(() => {
        if (numOfFailedLogins >= 3) {
            setButtonDisabled(true);
        }
    }, [numOfFailedLogins])

    useEffect(() => {
        let accessToken = localStorage.getItem("accessToken");
        if (accessToken && !isTokenExpired(accessToken)) {
            dispatch(setToken(accessToken));
        }
    }, [dispatch])

    useEffect(() => {
        if (loginState.userToken && !isTokenExpired(loginState.userToken)) {
            localStorage.removeItem("accessToken");
            localStorage.setItem("accessToken", loginState.userToken);
        }
        if (loginState.unsuccessfulGoogleError) {
            setErrorStatus(true, loginState.unsuccessfulGoogleError)
        }
    }, [loginState, loginState.userToken, loginState.unsuccessfulGoogleError])

    const onFinish = (values: any) => {
        if (!isSuccess) {
            // Check username, password và gửi OTP
            login({ uname: values.username, pwd: values.password, typeUsername: currentKey }).unwrap().then(res => {
                setErrorStatus(false);
                localStorage.setItem("numOfFailedLogins", "0");
            }).catch(err => {
                setErrorStatus(true, err.data?.message || "Đăng nhập thất bại!");
                if (recaptchaRef.current) {
                    recaptchaRef.current.reset();
                }
                if (numOfFailedLogins + 1 >= 3) {
                    setButtonDisabled(true);
                }

                localStorage.setItem("numOfFailedLogins", (numOfFailedLogins + 1).toString());

            })
        } else if (data && isSuccess) {
            // Check OTP và đăng nhập vào hệ thống
            verifyOtpLogin({ otp: values.otp, email: data.email }).unwrap().then(res => {
                setErrorStatus(false);
                localStorage.removeItem("accessToken");
                localStorage.setItem("accessToken", res.accessToken);
                trigger({}).unwrap().then(res => {
                    localStorage.setItem("numOfFailedLogins", "0");
                    dispatch(setUser(res))
                    navigate(0);
                })
            }).catch(err => {
                setErrorStatus(true, err.data?.message || "OTP không hợp lệ!");
            })
        }

    };

    const onValidate = (token: string | null) => {
        if (token) {
            verifyCaptcha(token).unwrap().then(res => {
                if (res.success) {
                    setButtonDisabled(false);
                } else {
                    notification.error({ message: "Lỗi Captcha. Vui lòng thử lại sau!" });
                }
            }).catch(err => {
                console.log(err);
                notification.error({ message: "Lỗi Captcha. Vui lòng thử lại sau!" });
            })
        } else {
            setButtonDisabled(true);
        }
    }

    const onResendOtpClick = () => {
        if (data?.email) {
            setErrorStatus(false);
            resendOtp({ email: data.email, action: OTPActions.LOGIN });
        } else {
            setErrorStatus(true, "Không gửi được mã OTP.")
        }
    }

    const onTabChange = (key: string) => {
        setCurrentKey(Number(key));
        switch (key) {
            case typeUsername.EMAIL.toString():
                setPlaceholder("Email");
                break;
            case typeUsername.PHONE.toString():
                setPlaceholder("Số điện thoại");
                break;
            case typeUsername.SO_CMND.toString():
                setPlaceholder("Số CMND/CCCD/Số hộ chiếu");
                break;
            case typeUsername.SO_DKKD.toString():
                setPlaceholder("Số ĐKKD");
                break;
            default:
                setPlaceholder("Email");
                break;
        }
    }

    return (
        <Form
            className="custom-form login-form"
            // style={{ marginTop: 35, marginBottom: 45 }}
            name="login"
            form={form}
            colon={false}
            onFinish={onFinish}
        >
            {!isSuccess ?
                <React.Fragment key="login">
                    <Row align="middle" justify="center">
                        <Col span={17}>
                            <Tabs onChange={onTabChange} centered className="login-tabs">
                                <Tabs.TabPane tab="Email" key={typeUsername.EMAIL}></Tabs.TabPane>
                                <Tabs.TabPane tab="SĐT" key={typeUsername.PHONE}></Tabs.TabPane>
                                <Tabs.TabPane tab="CMT/CCCD/Số hộ chiếu" key={typeUsername.SO_CMND}></Tabs.TabPane>
                                <Tabs.TabPane tab="Số ĐKKD" key={typeUsername.SO_DKKD}></Tabs.TabPane>
                            </Tabs>
                        </Col>
                    </Row>

                    <Row align="middle" justify="center" style={{ marginTop: 20 }}>
                        <Col span={12}>
                            <Tooltip title={MESSAGE} overlayInnerStyle={{ textAlign: 'center' }}>
                                <Form.Item
                                    name="username"
                                    rules={[
                                        { required: true, message: "Vui lòng nhập tên tài khoản." }
                                    ]}
                                >
                                    <FloatInput label={placeholder} placeholder={placeholder} />
                                </Form.Item>
                            </Tooltip>
                        </Col>
                    </Row>
                    <Row align="middle" justify="center">
                        <Col span={12}>
                            <Form.Item
                                name="password"
                                rules={[
                                    { required: true, message: "Vui lòng nhập mật khẩu." }
                                ]}
                            >
                                <FloatInput label="Mật khẩu" placeholder="Mật khẩu" isPassword />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row align="middle" justify="center">
                        <Col span={10} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Form.Item
                                noStyle
                                name="remember"
                                valuePropName="checked"
                            >
                                <Checkbox className="custom-checkbox">Nhớ tài khoản</Checkbox>
                            </Form.Item>
                            <span className="anchor-text" onClick={openForgotPassword}>Quên mật khẩu?</span>
                        </Col>
                    </Row>
                    {numOfFailedLogins >= 3 ?
                        <Row align="middle" justify="center" style={{ marginTop: 15 }}>
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={process.env.REACT_APP_RECAPTCHA_KEY || ""}
                                onChange={onValidate}
                                hl="vi"
                            />
                        </Row> : <></>}
                    <Row style={{ marginTop: 25 }} align="middle" justify="center">
                        <Button
                            disabled={buttonDisabled || isLoading}
                            className={buttonDisabled ? "button-light-gray" : "button-light-yellow"}
                            style={{ height: 50, width: 240, fontSize: 16 }}
                            htmlType="submit"
                            loading={isLoading}>ĐĂNG NHẬP</Button>
                    </Row>
                    <Row style={{ marginTop: 20 }} align="middle" justify="center">
                        <Button
                            style={{ height: 50, width: 240, fontSize: 14, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            href={`https://accounts.google.com/o/oauth2/auth/authorize?response_type=code&client_id=600394471661-vjfg164gbgqtarom7udpoe26t8n6hg77.apps.googleusercontent.com&redirect_uri=${URI}&scope=openid email`}
                        >
                            Đăng nhập bằng Google</Button>
                    </Row>
                    <Row style={{ marginTop: 35 }} align="middle" justify="center">
                        <span>Bạn chưa có tài khoản?<span className="anchor-text" onClick={openRegister}> Đăng ký ngay</span></span>
                    </Row>
                </React.Fragment> :
                <React.Fragment key="otp">
                    <Row align="middle" justify="center" style={{ marginTop: 20 }}>
                        <Col span={12}>
                            <Form.Item
                                style={{ marginBottom: 5 }}
                                name="otp"
                                rules={[
                                    { required: true, message: "Vui lòng nhập mã OTP." }
                                ]}
                            >
                                <FloatInput
                                    label="Nhập mã OTP"
                                    placeholder="Nhập mã OTP"
                                    maxLength={8} />
                            </Form.Item>
                            <div
                                className="anchor-text"
                                style={{ color: "#1890ff", marginBottom: 10, textAlign: 'right' }}
                                onClick={onResendOtpClick}
                            >Gửi lại mã OTP?</div>
                        </Col>
                    </Row>
                    <Row style={{ marginTop: 25 }} align="middle" justify="center">
                        <Button
                            disabled={verifyResponse.isLoading || resendOtpResponse.isLoading}
                            className={verifyResponse.isLoading ? "button-light-gray" : "button-light-yellow"}
                            style={{ height: 50, width: 240, fontSize: 16 }}
                            htmlType="submit"
                            loading={verifyResponse.isLoading || resendOtpResponse.isLoading}>XÁC NHẬN</Button>
                    </Row>
                </React.Fragment>
            }
        </Form>
    )
}

export default LoginForm